import { Routes } from '@angular/router';
import { OnboardingPageComponent } from './client-onboarding/components/onboarding-page/onboarding-page.component';
import { PricingPageComponent } from './pages/pricing/components/pricing-page/pricing-page.component';
import { HomePageComponent } from './pages/home/home-page/home-page.component';
import { ContactPageComponent } from './pages/contact/contact-page/contact-page.component';
import { LoginPageComponent } from './pages/login/login-page/login-page.component';
import { CommunitiesPageComponent } from './pages/communities/communities-page/communities-page.component';
import { PhysicianEntrepreneursPageComponent } from './pages/communities/physician-entrepreneurs-page/physician-entrepreneurs-page.component';
import { MedicalSocietiesPageComponent } from './pages/communities/medical-societies-page/medical-societies-page.component';
import { StartACommunityPageComponent } from './pages/start-a-community/start-a-community-page.component';
import { MobileAppPageComponent } from './pages/mobile-app/mobile-app-page.component';
import { AssociationManagementCompaniesPageComponent } from './pages/association-management-companies/association-management-companies-page.component';
import { StatusPageComponent } from './pages/status/status-page/status-page.component';
import { GATED_RESOURCES } from './pages/resources/gated/gated-resources.data';

// Define onboarding routes that should always be accessible
const onboardingRoutes: Routes = [
    {
        path: 'register-organization',
        component: OnboardingPageComponent,
        data: { title: 'MemberTrace | Register', description: 'Get your organization set up with MemberTrace in minutes. Secure, efficient, and user-friendly onboarding for seamless member management and engagement tracking.' }
    }
];

// Define all other routes
const otherRoutes: Routes = [
    {
        path: 'auth/login',
        redirectTo: 'login',
        pathMatch: 'full'
    },
    {
        path: '',
        component: HomePageComponent,
        data: { title: 'MemberTrace — Membership software for medical & professional societies', description: 'Membership software for medical and professional societies: renewals, annual meetings, CME events, dues, applications, and member directories in one branded portal. 90-day free trial.' }
    },
    {
        path: 'for-operators',
        component: AssociationManagementCompaniesPageComponent,
        data: { title: 'MemberTrace | For operators', description: 'For teams managing multiple member organizations: standardize member operations, events, payments, and reporting across the organizations you support—one operating layer for your team, polished experiences for each client.' }
    },
    {
        path: 'association-management-companies',
        redirectTo: 'for-operators',
        pathMatch: 'full'
    },
    {
        path: 'pricing',
        component: PricingPageComponent,
        data: { title: 'MemberTrace | Pricing', description: 'Simple pricing for membership management. Choose Society, Growth, or Operator, then add Managed Support if you want hands-on help with MemberTrace administration.' }
    },
    {
        path: 'mobile',
        component: MobileAppPageComponent,
        data: {
            title: 'MemberTrace | Mobile app',
            description: 'MemberTrace mobile: one shared app for members across organizations, plus optional white-glove branded apps for Enterprise. Your logo and colors are included on every plan.'
        }
    },
    {
        path: 'privacy',
        loadComponent: () => import('./pages/privacy/privacy-page.component').then((m) => m.PrivacyPageComponent),
        data: {
            title: 'MemberTrace | Privacy Policy',
            description: 'Learn how MemberTrace collects, uses, shares, and protects personal information across its websites, portals, and mobile applications.'
        }
    },
    {
        path: 'terms',
        loadComponent: () => import('./pages/terms/terms-page.component').then((m) => m.TermsPageComponent),
        data: {
            title: 'MemberTrace | Terms of Service',
            description: 'The terms that govern use of MemberTrace websites, portals, and mobile applications, including connected third-party accounts.'
        }
    },
    {
        path: 'data-deletion',
        loadComponent: () => import('./pages/data-deletion/data-deletion-page.component').then((m) => m.DataDeletionPageComponent),
        data: {
            title: 'MemberTrace | Data Deletion Instructions',
            description: 'How to disconnect a Facebook, Instagram, LinkedIn, X, YouTube, or RSS account from MemberTrace and delete the data stored for that connection.'
        }
    },
    {
        path: 'contact',
        component: ContactPageComponent,
        data: { title: 'MemberTrace | Contact', description: 'Get in touch with the MemberTrace team. We\'re here to help with questions, demos, pricing, and support for your member management needs.' }
    },
    {
        path: 'communities',
        component: CommunitiesPageComponent,
        data: { title: 'MemberTrace | Communities', description: 'MemberTrace powers professional communities: medical societies, physician entrepreneur networks, and membership organizations. See how we support communities like yours.' }
    },
    {
        path: 'communities/physician-entrepreneurs',
        component: PhysicianEntrepreneursPageComponent,
        data: { title: 'MemberTrace | Physician Entrepreneur Communities', description: 'Membership software built for physician entrepreneur communities. Manage members, events, and private physician networks with MemberTrace.' }
    },
    {
        path: 'communities/medical-societies',
        component: MedicalSocietiesPageComponent,
        data: { title: 'MemberTrace | Medical & Surgical Societies', description: 'Membership software for medical and surgical societies. Run renewals, annual meetings, CME events, and member directories in one platform.' }
    },
    {
        path: 'start-a-community',
        component: StartACommunityPageComponent,
        data: { title: 'MemberTrace | Start a Community', description: 'Start a professional community. MemberTrace provides the tools to launch and grow your network—membership, events, directories, and more.' }
    },
    {
        path: 'login',
        component: LoginPageComponent,
        data: { title: 'MemberTrace | Login', description: 'Sign in to your MemberTrace account to access your member management dashboard, reports, and tools.' }
    },
    {
        path: 'status',
        component: StatusPageComponent,
        data: {
            title: 'MemberTrace | System status',
            description: 'Live system status for MemberTrace: API health, uptime, and incident information.'
        }
    },
    {
        path: 'resources',
        loadComponent: () => import('./pages/resources/resources-index.component').then((m) => m.ResourcesIndexComponent),
        data: {
            title: 'MemberTrace | Resources',
            description: 'White papers, guides, and field notes for people who run professional communities.'
        }
    },
    // Gated downloads (form + PDF). Listed before resources/:slug so they win the match.
    ...GATED_RESOURCES.map((resource) => ({
        path: `resources/${resource.slug}`,
        loadComponent: () => import('./pages/resources/gated/gated-resource-page.component').then((m) => m.GatedResourcePageComponent),
        data: { title: resource.title, description: resource.description, gatedSlug: resource.slug }
    })),
    {
        path: 'resources/:slug',
        loadComponent: () => import('./pages/resources/resource-article.component').then((m) => m.ResourceArticleComponent),
        data: {
            title: 'MemberTrace | Resources',
            description: 'Long-form MemberTrace writing for professional communities.'
        }
    },
    {
        path: 'security',
        loadComponent: () => import('./pages/security/security-page.component').then((m) => m.SecurityPageComponent),
        data: {
            title: 'MemberTrace | Security & data handling',
            description: 'How MemberTrace hosts, protects, and handles member data: AWS hosting, encryption in transit and at rest, access control, Stripe payments, and backups.'
        }
    },
    {
        path: 'case-studies/:slug',
        loadComponent: () => import('./pages/case-studies/case-study-page.component').then((m) => m.CaseStudyPageComponent),
        data: {
            title: 'MemberTrace | Case study',
            description: 'How a member organization runs on MemberTrace.',
            robots: 'noindex, nofollow'
        }
    },
    {
        path: '404',
        loadComponent: () => import('./pages/not-found/not-found-page.component').then((m) => m.NotFoundPageComponent),
        data: { title: 'MemberTrace | Page not found', description: 'The page you were looking for could not be found.', robots: 'noindex' }
    },
    {
        path: '**',
        loadComponent: () => import('./pages/not-found/not-found-page.component').then((m) => m.NotFoundPageComponent),
        data: { title: 'MemberTrace | Page not found', description: 'The page you were looking for could not be found.', robots: 'noindex' }
    }
];

export const routes: Routes = [
    ...onboardingRoutes,
    ...otherRoutes
];
